import { TextInput } from '@octano/global-ui';
import { Fragment, useEffect, useMemo } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Col, Row } from 'reactstrap';
import {
  icfesFormatFields,
  IcfesFormatType,
} from '../../../utils/selectOptions';
import { useAsyncOptionsFieldsLoader } from '../../../views/StudentFileAsyncOptions/AsyncOptionsFieldsLoader';
import { SectionTitle } from '../../text';

type EmergencyContactReformadaSectionProps = {
  prefix: string;
  optionalFields?: string[];
  fieldNames?: {
    country: string;
    format: string;
    gradeNaturalSciences: string;
    gradeSocial: string;
    gradeLenguaje: string;
    gradeMathematics: string;
    gradeElective: string;
    gradeBiology: string;
    gradePhilosophy: string;
    gradePhysics: string;
    gradeHistory: string;
    gradeChemistry: string;
    gradeGeography: string;
    gradeInterdisciplinary: string;
    gradeEnglish: string;
    gradeCriticalReading: string;
    gradeCitizenship: string;
    gradeQuantitativeReasoning: string;
    gradeCivicCompetences: string;
    gradeGlobal: string;
    gradePosition: string;
  };
  disabled?: boolean;
};

/**
 * Para usar este componente tu formulario debe estar envuelto en su FormProvider
 * para poder acceder a la información del formulario a través de useFormContext
 * Los nombres agregados en fieldNames se relacionan a los campos que utilizaste
 * al definir tu formulario.
 */
const DynamicIcfesGradesReformada = ({
  prefix,
  optionalFields = [],
  fieldNames = {
    country: 'icfes_country',
    format: 'icfes_format',
    gradeNaturalSciences: 'gradeNaturalSciences',
    gradeSocial: 'gradeSocial',
    gradeLenguaje: 'gradeLenguaje',
    gradeMathematics: 'gradeMathematics',
    gradeElective: 'gradeElective',
    gradeBiology: 'gradeBiology',
    gradePhilosophy: 'gradePhilosophy',
    gradePhysics: 'gradePhysics',
    gradeHistory: 'gradeHistory',
    gradeChemistry: 'gradeChemistry',
    gradeGeography: 'gradeGeography',
    gradeInterdisciplinary: 'gradeInterdisciplinary',
    gradeEnglish: 'gradeEnglish',
    gradeCriticalReading: 'gradeCriticalReading',
    gradeCitizenship: 'gradeCitizenship',
    gradeQuantitativeReasoning: 'gradeQuantitativeReasoning',
    gradeCivicCompetences: 'gradeCivicCompetences',
    gradeGlobal: 'gradeGlobal',
    gradePosition: 'gradePosition',
  },
  disabled = false,
}: EmergencyContactReformadaSectionProps) => {
  const { t } = useTranslation();
  const { control, watch, formState, setValue } = useFormContext();
  const {
    data: { tenanCountry } = {
      data: [],
    },
  } = useAsyncOptionsFieldsLoader();

  const [selectedCountry, selectedFormat] = watch([
    fieldNames.country,
    fieldNames.format,
  ]);

  const isFormatTouched = formState.touchedFields[fieldNames.format];
  const isCountryTouched = formState.touchedFields[fieldNames.country];

  // Effects
  useEffect(() => {
    if (isFormatTouched || isCountryTouched) {
      setValue(fieldNames.gradeNaturalSciences, null);
      setValue(fieldNames.gradeSocial, null);
      setValue(fieldNames.gradeLenguaje, null);
      setValue(fieldNames.gradeMathematics, null);
      setValue(fieldNames.gradeElective, null);
      setValue(fieldNames.gradeBiology, null);
      setValue(fieldNames.gradePhilosophy, null);
      setValue(fieldNames.gradePhysics, null);
      setValue(fieldNames.gradeHistory, null);
      setValue(fieldNames.gradeChemistry, null);
      setValue(fieldNames.gradeGeography, null);
      setValue(fieldNames.gradeInterdisciplinary, null);
      setValue(fieldNames.gradeEnglish, null);
      setValue(fieldNames.gradeCriticalReading, null);
      setValue(fieldNames.gradeCitizenship, null);
      setValue(fieldNames.gradeQuantitativeReasoning, null);
      setValue(fieldNames.gradeCivicCompetences, null);
      setValue(fieldNames.gradeGlobal, null);
      setValue(fieldNames.gradePosition, null);
    }
  }, [
    selectedFormat,
    selectedCountry,
    setValue,
    isFormatTouched,
    isCountryTouched,
    fieldNames.gradeNaturalSciences,
    fieldNames.gradeSocial,
    fieldNames.gradeLenguaje,
    fieldNames.gradeMathematics,
    fieldNames.gradeElective,
    fieldNames.gradeBiology,
    fieldNames.gradePhilosophy,
    fieldNames.gradePhysics,
    fieldNames.gradeHistory,
    fieldNames.gradeChemistry,
    fieldNames.gradeGeography,
    fieldNames.gradeInterdisciplinary,
    fieldNames.gradeEnglish,
    fieldNames.gradeCriticalReading,
    fieldNames.gradeCitizenship,
    fieldNames.gradeQuantitativeReasoning,
    fieldNames.gradeCivicCompetences,
    fieldNames.gradeGlobal,
    fieldNames.gradePosition,
  ]);

  // Memo
  const isSectionDisabled = useMemo(() => {
    return selectedCountry?.value === tenanCountry?.id;
  }, [selectedCountry?.value, tenanCountry]);

  const fields = useMemo(() => {
    if (!isSectionDisabled) {
      return <></>;
    }

    return (
      <Fragment>
        <Row className="pt-5">
          <Col xs={12}>
            <SectionTitle text={t(`${prefix}.title`)} />
          </Col>
        </Row>
        <Row>
          {(
            icfesFormatFields[selectedFormat?.value as IcfesFormatType] ?? []
          ).map((grade) => (
            <Col className="pb-3" xs={12} lg={4}>
              <TextInput
                name={`${grade}`}
                label={t(`${prefix}.${grade}`)}
                control={control}
                shouldUnregister={true}
              />
            </Col>
          ))}
        </Row>
      </Fragment>
    );
  }, [selectedFormat?.value, t, isSectionDisabled, control, prefix]);

  return <>{fields}</>;
};

export default DynamicIcfesGradesReformada;
